import { computed, type Ref, ref } from 'vue'
import { type MemorialGiftDTO, type MultiLang } from './memorize'
import { defineStore } from 'pinia'
import { CommonResponse, GIFT_LIST, netClient } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import type { FileDTO } from '../media/media'
import { useLocalStorage } from '@vueuse/core'

export interface GiftCategory {
  value: MultiLang
  main: boolean
  rank: number
  hide: boolean
}

export interface GiftShortcutDTO {
  type: number
  fileId: string
  giftCategory: string
  giftId: string
  name: string
  file: FileDTO
}

export interface GetGiftResponse {
  categories: Array<GiftCategory>
  gifts: Array<MemorialGiftDTO>
  shortcut: GiftShortcutDTO
}

function getGifts() {
  return netClient.request<GetGiftResponse>(GIFT_LIST, 'get', {})
}

export const useGiftStore = defineStore('gift', () => {
  // 持久化存储配置
  const storage = useLocalStorage(
    'giftStore',
    {
      giftList: [] as MemorialGiftDTO[],
      categories: [] as GiftCategory[],
      shortcut: null as GiftShortcutDTO | null,
      state: 'loading' as 'loading' | 'loaded' | 'failed',
      lastUpdateAt: null as number | null // 使用时间戳存储
    },
    {
      serializer: {
        read: (v) => {
          const raw = v ? JSON.parse(v) : {}
          return {
            giftList: raw.giftList || [],
            categories: raw.categories || [],
            shortcut: raw.shortcut || null,
            state: raw.state || 'loading',
            lastUpdateAt: raw.lastUpdateAt ? new Date(raw.lastUpdateAt).getTime() : null
          }
        },
        write: (v) =>
          JSON.stringify({
            ...v,
            lastUpdateAt: v.lastUpdateAt ? new Date(v.lastUpdateAt).toISOString() : null
          })
      }
    }
  )

  // 响应式状态
  const giftList = computed({
    get: () => storage.value.giftList,
    set: (val) => (storage.value.giftList = val)
  })

  const categories = computed({
    get: () => storage.value.categories,
    set: (val) => (storage.value.categories = val)
  })

  const shortcut = computed({
    get: () => storage.value.shortcut,
    set: (val) => (storage.value.shortcut = val)
  })

  const state = computed({
    get: () => storage.value.state,
    set: (val) => (storage.value.state = val)
  })

  const lastUpdateAt = computed({
    get: () => (storage.value.lastUpdateAt ? new Date(storage.value.lastUpdateAt) : null),
    set: (val) => (storage.value.lastUpdateAt = val?.getTime() || null)
  })

  // 操作方法
  const updateLifeList = (newList: MemorialGiftDTO[], newCategories: GiftCategory[]) => {
    giftList.value = [...newList]
    categories.value = [...newCategories]
    state.value = 'loaded'
    lastUpdateAt.value = new Date()
  }

  const setShortcut = (newShortcut: GiftShortcutDTO) => {
    shortcut.value = { ...newShortcut }
  }

  const loadFailed = () => {
    state.value = 'failed'
    giftList.value = []
    categories.value = []
  }

  const loading = () => {
    state.value = 'loading'
  }

  // 计算属性
  const getGiftList = computed(() => giftList.value)
  const getCategories = computed(() => categories.value)
  const getState = computed(() => state.value)
  const getLastUpdateAt = computed(() => lastUpdateAt.value)
  const getShortcut = computed(() => shortcut.value)

  return {
    giftList,
    categories,
    shortcut,
    state,
    lastUpdateAt,
    updateLifeList,
    setShortcut,
    loadFailed,
    loading,
    getGiftList,
    getCategories,
    getState,
    getLastUpdateAt,
    getShortcut
  }
})

// 获取礼物列表
export async function tryGetGifts() {
  const store = useGiftStore()
  const currentTimestamp = new Date().getTime()
  const lastUpdateTimestamp = store.getLastUpdateAt?.getTime() || 0
  const timeDifference = currentTimestamp - lastUpdateTimestamp
  console.log('当前礼物缓存！', currentTimestamp, lastUpdateTimestamp, timeDifference)

  if (store.getGiftList.length === 0 || store.getLastUpdateAt === null || timeDifference > 60000) {
    const { error, data, statusCode } = await getGifts()
    if (error.value) {
      store.loadFailed()
    } else {
      const resData = data as unknown as Ref<CommonResponse<GetGiftResponse>>
      if (resData.value.code === CODE_OK) {
        const giftsRes = resData.value.data
        store.updateLifeList(giftsRes.gifts, giftsRes.categories)
        store.setShortcut(giftsRes.shortcut)
      } else {
        store.loadFailed()
        console.log('get gifts failed:', resData.value.code, resData.value.message)
      }
    }
  }
}

// ==== gift的播放
export const gifts2Play: Ref<Array<MemorialGiftDTO>> = ref([])
const _showGiftPlay = ref<boolean>(false)
export const showGiftPlay = _showGiftPlay
